import * as Dialog from "@radix-ui/react-dialog";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuizStore } from "../state/store";
import { useAuthStore } from "../state/authStore";
import { wipeLocalAll } from "../lib/hydrate";
import { ResetDialog } from "./ResetDialog";
import { DangerDialog } from "./admin/DangerDialog";
import { BrandMark } from "./BrandMark";
import { playCorrect, unlockAudio } from "../lib/feedback";
import type { LearningDirection, LearningMode, ThemePref } from "../lib/types";

type Props = {
  trigger: ReactNode;
};

export function SettingsSheet({ trigger }: Props) {
  const [open, setOpen] = useState(false);
  const settings = useQuizStore((s) => s.settings);
  const setTheme = useQuizStore((s) => s.setTheme);
  const setSound = useQuizStore((s) => s.setSound);
  const setLearningDirection = useQuizStore((s) => s.setLearningDirection);
  const setLearningMode = useQuizStore((s) => s.setLearningMode);
  const markersSize = useQuizStore((s) => s.markers.size);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const deleteAccount = useAuthStore((s) => s.deleteAccount);
  const navigate = useNavigate();

  function openStats() {
    navigate("/stats");
    setOpen(false);
  }

  function openHelp() {
    navigate("/help");
    setOpen(false);
  }

  function openWords() {
    navigate("/words");
    setOpen(false);
  }

  async function onLogout() {
    setOpen(false);
    await logout();
    await wipeLocalAll();
    navigate("/login", { replace: true });
  }

  async function onSelfDelete() {
    // Wipe local *before* navigating so any final React render sees clean state.
    await deleteAccount();
    await wipeLocalAll();
    setOpen(false);
    navigate("/login?deleted=1", { replace: true });
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
        {/*
          Single-scroll sheet: the whole content scrolls together under one
          `overflow-y-auto`. max-h-[90dvh] uses the dynamic viewport so iOS
          Safari's URL-bar collapse doesn't push the bottom row off-screen.
          (Earlier tried a sticky-header/sticky-footer split — Doron preferred
          the simpler whole-sheet scroll on real iPhone testing, issue #62.)
        */}
        <Dialog.Content
          className="
            fixed z-50 bg-elevated shadow-modal p-5 space-y-5
            max-h-[90dvh] overflow-y-auto overscroll-contain
            [-webkit-overflow-scrolling:touch]
            left-0 right-0 bottom-0 rounded-t-sheet border-t border-outline-strong animate-sheet-up
            sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:right-auto sm:w-[440px] sm:max-h-[85vh]
            sm:-translate-x-1/2 sm:-translate-y-1/2
            sm:rounded-sheet sm:border sm:animate-slide-up
            safe-bottom
          "
          data-testid="settings-sheet"
        >
          <div className="sm:hidden mx-auto -mt-1 mb-2 h-1.5 w-10 rounded-full bg-outline" aria-hidden />

          <Dialog.Title className="text-lg font-bold text-fg">Menü</Dialog.Title>

          <section className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-fg-faint">Lernrichtung</label>
              <DirectionPicker
                value={settings.learningDirection}
                onChange={(d) => void setLearningDirection(d)}
              />
            </section>

            <section className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-fg-faint">Modus</label>
              <ModePicker
                value={settings.learningMode}
                markedCount={markersSize}
                onChange={(m) => void setLearningMode(m)}
              />
            </section>

          <section className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-fg-faint">Design</label>
              <ThemePicker value={settings.theme} onChange={(t) => void setTheme(t)} />
            </section>

            <section className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-fg-faint">Feedback</label>
              <ToggleRow
                label="Ton"
                hint="Tonchen bei richtig/falsch"
                checked={settings.sound}
                onChange={(v) => {
                  // The toggle itself is a user gesture — unlock Web Audio here so
                  // the first answer click on iOS already has a resumed context.
                  if (v) unlockAudio();
                  void setSound(v);
                }}
                testid="toggle-sound"
              />
              <button
                type="button"
                onClick={() => {
                  unlockAudio();
                  playCorrect();
                }}
                className="w-full min-h-[44px] px-4 py-2 rounded-lg bg-bg/40 border border-outline-soft text-fg-muted text-xs hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between gap-2"
                data-testid="test-sound"
              >
                <span>Ton testen</span>
                <span className="text-fg-faint" aria-hidden>♪</span>
              </button>
              <p className="text-[11px] text-fg-faint leading-snug px-1">
                Auf iPhone: Stummschalter an der Seite ausschalten — sonst bleibt der Ton aus, auch
                wenn er hier aktiviert ist.
              </p>
            </section>

            <section className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-fg-faint">Mehr</label>
              <button
                type="button"
                onClick={openWords}
                className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-outline text-fg text-sm font-semibold text-left hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between"
                data-testid="open-words"
              >
                <span>
                  Meine Vokabeln{markersSize > 0 && (
                    <span className="ml-1.5 text-xs text-warning font-semibold">
                      · ★ {markersSize}
                    </span>
                  )}
                </span>
                <span className="text-fg-muted">→</span>
              </button>

              <button
                type="button"
                onClick={openStats}
                className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-outline text-fg text-sm font-semibold text-left hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between"
                data-testid="open-stats"
              >
                <span>Statistik anzeigen</span>
                <span className="text-fg-muted">→</span>
              </button>

              <button
                type="button"
                onClick={openHelp}
                className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-outline text-fg text-sm font-semibold text-left hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between"
                data-testid="open-help"
              >
                <span>Wie funktioniert das?</span>
                <span className="text-fg-muted">→</span>
              </button>

              <ResetDialog
                trigger={
                  <button
                    type="button"
                    className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-outline text-danger text-sm font-semibold text-left hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-danger flex items-center justify-between"
                    data-testid="open-reset"
                  >
                    <span>Fortschritt zurücksetzen</span>
                    <span className="text-danger/70">⚠</span>
                  </button>
                }
              />
            </section>

            {user && (
              <section className="space-y-2 pt-1 border-t border-outline-soft">
                <label className="text-xs uppercase tracking-widest text-fg-faint">Konto</label>
                <div className="px-1">
                  <div className="text-sm font-medium text-fg" data-testid="account-email">
                    {user.email}
                  </div>
                  <div className="text-xs text-fg-faint capitalize">{user.role}</div>
                </div>
                <button
                  type="button"
                  onClick={() => void onLogout()}
                  data-testid="logout-button"
                  className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-outline text-fg text-sm font-semibold text-left hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between"
                >
                  <span>Abmelden</span>
                  <span className="text-fg-muted" aria-hidden>⎋</span>
                </button>
                {user.role !== "admin" && (
                  <DangerDialog
                    title="Konto unwiderruflich löschen?"
                    description={
                      <span>
                        Dein Konto wird sofort gelöscht. Lernfortschritt, Antwort-Verlauf und alle aktiven Sessions werden entfernt. Du kannst dich danach jederzeit mit einem <strong>neuen Einladungscode</strong> wieder registrieren — dein bisheriger Fortschritt ist dann aber nicht mehr wiederherstellbar.
                      </span>
                    }
                    confirmPhrase={user.email}
                    confirmLabel="Konto löschen"
                    onConfirm={onSelfDelete}
                    trigger={
                      <button
                        type="button"
                        data-testid="delete-account-button"
                        className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-surface border border-danger/40 text-danger text-sm font-semibold text-left hover:bg-danger-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-danger flex items-center justify-between"
                      >
                        <span>Konto löschen (DSGVO)</span>
                        <span className="text-danger/70" aria-hidden>✕</span>
                      </button>
                    }
                  />
                )}
              </section>
            )}

          <Dialog.Close asChild>
            <button
              type="button"
              className="w-full min-h-[44px] px-4 py-2 rounded-lg bg-bg/40 border border-outline-soft text-fg-muted text-sm hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
              data-testid="settings-close"
            >
              Schließen
            </button>
          </Dialog.Close>

          <div
            className="pt-3 mt-2 border-t border-outline-soft flex flex-col items-center gap-1.5"
            data-testid="settings-brand-footer"
          >
            <BrandMark variant="horizontal" className="h-5 w-auto opacity-80" />
            <nav className="flex gap-3 text-[11px] text-accent flex-wrap justify-center" aria-label="Rechtliches">
              <Link
                to="/impressum"
                data-testid="settings-imprint"
                onClick={() => setOpen(false)}
                className="hover:underline"
              >
                Impressum
              </Link>
              <span className="text-fg-faintest" aria-hidden>·</span>
              <Link
                to="/datenschutz"
                data-testid="settings-privacy"
                onClick={() => setOpen(false)}
                className="hover:underline"
              >
                Datenschutz
              </Link>
              <span className="text-fg-faintest" aria-hidden>·</span>
              <Link
                to="/haftung"
                data-testid="settings-disclaimer"
                onClick={() => setOpen(false)}
                className="hover:underline"
              >
                Haftung
              </Link>
            </nav>
            <span
              className="text-[10px] text-fg-faint tabular tracking-widest font-mono"
              data-testid="settings-version"
            >
              v{__APP_VERSION__} · BUILD {__BUILD_DATE__}
            </span>
            <span className="text-[10px] text-fg-faint tracking-wide text-center">
              © 2026 Concept4IT Consulting GmbH
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DirectionPicker({
  value,
  onChange,
}: {
  value: LearningDirection;
  onChange: (d: LearningDirection) => void;
}) {
  const opts: { value: LearningDirection; label: string; sub: string }[] = [
    { value: "en_de", label: "EN → DE", sub: "Englisch lesen" },
    { value: "de_en", label: "DE → EN", sub: "Deutsch lesen" },
    { value: "mixed", label: "Gemischt", sub: "Beides zufällig" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Lernrichtung"
      className="grid grid-cols-3 gap-1.5 bg-bg/40 border border-outline-soft rounded-lg p-1"
    >
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`learning-direction-${o.value}`}
            className={`min-h-[52px] rounded-md text-xs font-semibold transition-colors flex flex-col items-center justify-center gap-0.5 px-2 leading-tight ${
              active
                ? "bg-accent text-accent-on shadow-card"
                : "text-fg-muted hover:text-fg hover:bg-card"
            }`}
          >
            <span>{o.label}</span>
            <span className={`text-[10px] ${active ? "text-accent-on/85" : "text-fg-faint"}`}>{o.sub}</span>
          </button>
        );
      })}
    </div>
  );
}

function ModePicker({
  value,
  markedCount,
  onChange,
}: {
  value: LearningMode;
  markedCount: number;
  onChange: (m: LearningMode) => void;
}) {
  const opts: { value: LearningMode; label: string; sub: string }[] = [
    { value: "all", label: "Alle Vokabeln", sub: "Ganzer Pool" },
    {
      value: "marked",
      label: "Markierte",
      sub: markedCount === 0 ? "Noch keine ★" : `${markedCount} markiert`,
    },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Lern-Modus"
      className="grid grid-cols-2 gap-1.5 bg-bg/40 border border-outline-soft rounded-lg p-1"
    >
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`learning-mode-${o.value}`}
            className={`min-h-[52px] rounded-md text-xs font-semibold transition-colors flex flex-col items-center justify-center gap-0.5 px-2 leading-tight ${
              active
                ? o.value === "marked"
                  ? "bg-warning text-bg shadow-card"
                  : "bg-accent text-accent-on shadow-card"
                : "text-fg-muted hover:text-fg hover:bg-card"
            }`}
          >
            <span>{o.value === "marked" ? `★ ${o.label}` : o.label}</span>
            <span
              className={`text-[10px] ${
                active
                  ? o.value === "marked"
                    ? "text-bg/85"
                    : "text-accent-on/85"
                  : "text-fg-faint"
              }`}
            >
              {o.sub}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ThemePicker({ value, onChange }: { value: ThemePref; onChange: (t: ThemePref) => void }) {
  const opts: { value: ThemePref; label: string; symbol: string }[] = [
    { value: "system", label: "Auto", symbol: "◐" },
    { value: "light", label: "Hell", symbol: "☀" },
    { value: "dark", label: "Dunkel", symbol: "☾" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Design"
      className="grid grid-cols-3 gap-1.5 bg-bg/40 border border-outline-soft rounded-lg p-1"
    >
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`theme-${o.value}`}
            className={`min-h-[44px] rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              active
                ? "bg-accent text-accent-on shadow-card"
                : "text-fg-muted hover:text-fg hover:bg-card"
            }`}
          >
            <span aria-hidden>{o.symbol}</span>
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  testid,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className="w-full min-h-[56px] px-4 py-3 rounded-lg bg-surface border border-outline hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong flex items-center justify-between gap-3 text-left transition-colors"
    >
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-fg">{label}</span>
        <span className="text-xs text-fg-muted">{hint}</span>
      </div>
      <span
        className={`shrink-0 inline-flex items-center w-11 h-6 rounded-full transition-colors ${
          checked ? "bg-accent" : "bg-outline-strong"
        }`}
        aria-hidden
      >
        <span
          className={`inline-block w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </span>
    </button>
  );
}
